import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  CircularProgress,
  Avatar,
  Stack,
  Pagination,
  Box,
  Tooltip
} from '@mui/material';
import palette from 'src/theme/palette';
import EditAddModal from './EditAddModal';
import { TableNoData } from 'src/components/NoData';
import { api } from 'src/Api';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import Iconify from 'src/components/Iconify';
import LoadingButtonWrapper from 'src/components/LoadingButtonWrapper';
import InviteModalUserManage from './InviteModalUserManage';
import UserDetailsDialog from './UserDetailsDialog';

const UserManageTable = ({ data, loading, refresh, handlePageChange, pagination, permission }) => {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));
  const [statusLoading, setStatusLoading] = useState(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleEditClick = (row) => {
    setEditingData(row);
    setEditModalOpen(true);
  };

  const handleInviteClick = (row) => {
    setEditingData(row);
    setInviteOpen(true);
  };

  function handleStatusChange(row) {
    setStatusLoading(row.id);
    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append('Authorization', `Bearer ${token}`);

    const newStatus = row.status?.toLowerCase() === 'active' ? 8 : 2; // inactive Status 8 / active Status 2

    const formData = new FormData();
    formData.append('user_id', row?.id);
    newStatus && formData.append('status_id', newStatus);

    const requestOptions = {
      method: 'Post',
      redirect: 'follow',
      headers: myHeaders,
      body: formData
    };

    fetch(api.update_status, requestOptions)
      .then((response) =>
        response.text().then((result) => ({ status: response.status, body: result }))
      )
      .then(({ status, body }) => {
        const result = JSON.parse(body);
        if (status === 200 || status === 201) {
          toast.success(result.message);
          refresh();
        } else {
          toast.error(result.error);
        }
      })
      .catch((error) => {
        toast.error(error.error);
        console.log('error', error);
      })
      .finally(() => {
        setStatusLoading(null);
      });
  }

  const handleUserClick = (row) => {
    setShowUserDetailModal(true);
    setSelectedUser(row);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)', px: 2 }}
      >
        <Table sx={{ padding: 0 }}>
          <TableHead>
            <TableRow sx={{ borderBottom: '1.5px solid #E5E7EB' }}>
              <TableCell
                sx={{
                  fontSize: 11,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 0,
                  minWidth: { xs: 220, md: 150, lg: 140 }
                }}
              >
                User
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 0,
                  minWidth: { xs: 150, md: 150, lg: 140 }
                }}
              >
                Contact
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 0,
                  minWidth: { xs: 100, md: 100, lg: 100 }
                }}
              >
                Role
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 12,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin',
                  pl: 0,
                  minWidth: { xs: 100, md: 150, lg: 140 }
                }}
              >
                Status
              </TableCell>

              <TableCell
                sx={{
                  minWidth: { xs: 180, md: 180 },
                  fontSize: 12,
                  pl: 0,
                  color: palette.primary.tableHeadTitle,
                  fontFamily: 'poppinsThin'
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          {!loading && (
            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id} sx={{ borderBottom: '1.5px solid #E5E7EB' }}>
                  <TableCell sx={{ pl: 0 }}>
                    <Stack spacing={2} direction="row" alignItems="center">
                      <Avatar
                        alt={row?.firstname + ' ' + row?.lastname}
                        src={row?.avatar}
                        sx={{ height: 40, width: 40 }}
                      />

                      <Stack
                        sx={{
                          flex: '1 1 auto',
                          alignItems: 'flex-start',
                          width: '120px'
                        }}
                      >
                        <Tooltip title={row?.firstname + ' ' + row?.lastname} placement="top" arrow>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontFamily: 'poppinsRegular',
                              color: palette.primary.main,
                              cursor: 'pointer',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: 'block',
                              maxWidth: '170px',
                              '&:hover': {
                                textDecoration: 'underline',
                                color: palette.primary.mainDark
                              }
                            }}
                            onClick={() => handleUserClick(row)}
                          >
                            {row?.firstname + ' ' + row?.lastname || 'N/A'}
                          </Typography>
                        </Tooltip>
                        <Typography
                          color="inherit"
                          sx={{
                            fontSize: 10,
                            fontFamily: 'poppinsMedium',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 170 // or any suitable width
                          }}
                        >
                          {row?.email || 'N/A'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontFamily: 'poppinsMedium',
                      pl: 0
                    }}
                  >
                    {row?.contact_no || 'N/A'}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 12,
                      fontFamily: 'poppinsMedium',
                      pl: 0
                    }}
                  >
                    {row?.role?.role_name || 'N/A'}
                  </TableCell>
                  <TableCell
                    sx={{
                      pl: 0
                    }}
                  >
                    <Typography
                      sx={{ fontFamily: 'poppinsLight' }}
                      className={
                        (row?.status?.toLowerCase() == 'active' && 'status-green') ||
                        (row?.status?.toLowerCase() == 'pending' && 'status-pending') ||
                        (row?.status?.toLowerCase() == 'deactive' && 'status-red')
                      }
                    >
                      {row?.status || 'N/A'}
                    </Typography>
                  </TableCell>

                  {/* ACTIONS */}
                  <TableCell sx={{ pl: 0, pr: 0, mr: 0 }}>
                    {permission.includes('user invite') && (
                      <Tooltip title="Invite">
                        <span>
                          <LoadingButtonWrapper
                            variant="contained"
                            sx={{
                              width: 70,
                              height: 30,
                              fontSize: 10,
                              borderRadius: 0.7,
                              fontFamily: 'poppinsLight',
                              letterSpacing: 0.3,
                              minWidth: 10,
                              paddingRight: 0,
                              paddingLeft: 0,
                              paddingTop: 0,
                              paddingBottom: 0,
                              mr: 0.5,
                              transition:
                                'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
                            }}
                            title="Invite"
                            onClick={() => handleInviteClick(row)}
                            startIcon={
                              <Iconify
                                style={{
                                  cursor: 'pointer',
                                  fontSize: 16,
                                  color: '#ffffff'
                                }}
                                icon="mingcute:invite-line"
                              />
                            }
                          />
                        </span>
                      </Tooltip>
                    )}

                    {permission.includes('edit') && (
                      <Tooltip title="Edit">
                        <span>
                          <LoadingButtonWrapper
                            disabled={row?.status?.toLowerCase() == 'pending' ? true : false}
                            variant="contained"
                            sx={{
                              width: 60,
                              height: 30,
                              fontSize: 10,
                              borderRadius: 0.7,
                              fontFamily: 'poppinsLight',
                              letterSpacing: 0.3,
                              minWidth: 10,
                              paddingRight: 0,
                              paddingLeft: 0,
                              paddingTop: 0,
                              paddingBottom: 0,
                              marginRight: 0.5,
                              transition:
                                'width 0.3s ease, background-color 0.3s ease, transform 0.3s ease'
                            }}
                            title="Edit"
                            onClick={() => handleEditClick(row)}
                            startIcon={
                              <Iconify
                                style={{
                                  cursor: 'pointer',
                                  fontSize: 16,
                                  color:
                                    row?.status?.toLowerCase() == 'pending'
                                      ? ' rgba(145, 158, 171, 0.6)'
                                      : '#ffffff'
                                }}
                                icon="cuida:edit-outline"
                              />
                            }
                          />
                        </span>
                      </Tooltip>
                    )}

                    {permission.includes('change status') && (
                      <LoadingButton
                        disabled={
                          statusLoading === row?.id || row?.status?.toLowerCase() == 'pending'
                        }
                        loading={statusLoading === row?.id}
                        variant="contained"
                        size="small"
                        onClick={() => handleStatusChange(row)}
                        sx={{
                          borderRadius: 0.7,
                          height: 30,
                          backgroundColor:
                            row?.status?.toLowerCase() === 'active'
                              ? '#f72929ff' // Red for Deactivate
                              : '#4CAF50', // Green for Activate
                          color: '#fff',
                          fontSize: 12,
                          fontFamily: 'poppinsLight',
                          textTransform: 'none',
                          marginRight: 0.5,
                          '&:hover': {
                            backgroundColor:
                              row?.status?.toLowerCase() === 'active' ? '#cb2020ff' : '#3F9142'
                          }
                        }}
                      >
                        {row?.status?.toLowerCase() === 'active' ? 'Deactivate' : 'Activate'}
                      </LoadingButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}

          {/* loader */}
          {loading && (
            <TableBody>
              <TableRow>
                <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            </TableBody>
          )}

          {/* empty list */}
          {data.length == 0 && !loading && <TableNoData colSpan={7} />}
        </Table>

        {/* PAGINATION */}
        {!loading && pagination.last_page > 1 && data.length !== 0 && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              marginTop: 10,
              alignItems: 'center'
            }}
            my={1}
            px={2}
          >
            <Pagination
              count={pagination.last_page}
              page={pagination.current_page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </TableContainer>

      <EditAddModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingData(null);
        }}
        data={editingData}
        isEdit={Boolean(editingData)}
        refresh={refresh}
      />

      <UserDetailsDialog
        open={showUserDetailModal}
        onClose={() => {
          setShowUserDetailModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      <InviteModalUserManage
        visiable={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
          setEditingData(null);
        }}
        data={editingData}
        isEdit={Boolean(editingData)}
        refresh={refresh}
      />
    </>
  );
};

export default UserManageTable;
